import BadgeBatchProgressController from "./badge_batch_progress_controller"

const COUNTER_COLUMNS = new Set(["total_count", "processed_count", "generated_count", "excluded_count"])
const COMPLETION_COLUMNS = new Set(["processed_count", "generated_count"])

export default class extends BadgeBatchProgressController {
    static targets = ["record"]

    connect() {
        this.counterFlashTimers = new Map()
        this.runningValue = this.pendingRecords.length > 0
        super.connect()
    }

    disconnect() {
        super.disconnect()
        this.counterFlashTimers.forEach((timer, cell) => {
            clearTimeout(timer)
            cell.classList.remove("badge-history-list-counter-cell--updated")
        })
        this.counterFlashTimers.clear()
    }

    get pendingRecords() {
        const records = new Map()
        this.recordTargets.forEach(record => {
            const running = record.getAttribute("data-running")
            if (running !== null && running !== "false" && running !== "0") {
                records.set(record.dataset.badgeHistoryListId, record)
            }
        })
        return [...records.values()]
    }

    get pollingUrl() {
        const url = new URL(this.urlValue, window.location.origin)
        this.pendingRecords.forEach(record => {
            url.searchParams.append("progress_ids[]", record.dataset.badgeHistoryListId)
        })
        return url.toString()
    }

    refresh() {
        this.runningValue = this.pendingRecords.length > 0
        return super.refresh()
    }

    applyProgress(data) {
        const updates = new Map(data.updates.map(update => [String(update.id), update]))
        const changedCounters = []
        this.pendingRecords.forEach(record => {
            const id = record.dataset.badgeHistoryListId
            const row = record.closest("tr")
            const update = updates.get(id)
            if (!row) return
            if (!update) {
                row.remove()
                return
            }

            record.dataset.running = String(update.running)
            Object.entries(update.cells).forEach(([column, html]) => {
                const cell = row.querySelector(`td[data-column="${column}"]`)
                const key = `${id}:${column}`
                if (!cell || this.fragments.get(key) === html) return

                const previousValue = cell.textContent.trim()
                cell.innerHTML = html
                this.fragments.set(key, html)
                if (COUNTER_COLUMNS.has(column) && previousValue !== cell.textContent.trim()) {
                    const total = Number(update.counts?.total_count ?? update.cells.total_count)
                    const value = Number(update.counts?.[column] ?? cell.textContent.trim())
                    const completed = COMPLETION_COLUMNS.has(column) && Number.isFinite(total) && total > 0 && value === total
                    changedCounters.push({ cell, completed })
                }
            })
            const deleteButton = row.querySelector("[data-badge-history-list-delete]")
            const deleteKey = `${id}:delete`
            if (deleteButton && typeof update.delete_button === "string" && this.fragments.get(deleteKey) !== update.delete_button) {
                deleteButton.innerHTML = update.delete_button
                this.fragments.set(deleteKey, update.delete_button)
            }
            row.querySelectorAll('[data-badge-batch-grid-target~="record"]').forEach(marker => {
                marker.dataset.running = String(update.running)
            })
        })
        changedCounters.forEach(({ cell, completed }) => this.flashCounter(cell, completed))
        this.runningValue = this.pendingRecords.length > 0
    }

    flashCounter(cell, completed) {
        clearTimeout(this.counterFlashTimers.get(cell))

        const range = document.createRange()
        range.selectNodeContents(cell)
        const textBounds = range.getBoundingClientRect()
        if (!textBounds.width || !textBounds.height) return

        const cellBounds = cell.getBoundingClientRect()
        const style = getComputedStyle(cell)
        const fontSize = Number.parseFloat(style.fontSize)
        const borderRight = Number.parseFloat(style.borderRightWidth) || 0
        const centerRight = cellBounds.right - textBounds.right + textBounds.width / 2 - borderRight
        const centerOffsetY = textBounds.top + textBounds.height / 2 - cellBounds.top - cell.clientTop - cell.clientHeight / 2
        const badge = document.createElement("span")
        badge.className = "badge rounded-circle badge-history-list-counter-highlight"
        badge.setAttribute("aria-hidden", "true")
        badge.style.setProperty("--bhl-counter-diameter", `${Math.max(fontSize * 1.3, textBounds.width + fontSize * 0.25)}px`)
        badge.style.setProperty("--bhl-counter-center-right", `${centerRight}px`)
        badge.style.setProperty("--bhl-counter-center-offset-y", `${centerOffsetY}px`)
        cell.classList.add("badge-history-list-counter-cell", "badge-history-list-counter-cell--updated")
        cell.classList.toggle("badge-history-list-counter-cell--completed", completed)
        cell.append(badge)
        this.counterFlashTimers.set(cell, setTimeout(() => {
            cell.classList.remove("badge-history-list-counter-cell--updated")
            this.counterFlashTimers.delete(cell)
        }, 2000))
    }
}
